/**
 * Sentinel Mapping Injection Engine
 *
 * Central registry of Online Program IDs, Pricing Plan IDs, and Renewal
 * Program IDs for every certification program. All backend API calls,
 * UI components, and headless collection lookups reference these mappings
 * by certification code (e.g. "LEP-C", "HEP-C").
 *
 * When real Wix IDs are configured, replace the placeholder values with
 * the actual Wix Online Program / Pricing Plan GUIDs. The validation
 * functions below ensure no API call is made with a missing or invalid ID.
 */

import { CERTIFICATION_PROGRAMS, type CertificationProgram } from "./certifications";

export interface ProgramMapping {
  industry: string;
  level: string;
  onlineProgramId: string;
  pricePlanId: string;
  renewalProgramId: string;
}

/** Build the mapping from the seeded program catalog. */
function buildMapping(): Record<string, ProgramMapping> {
  const map: Record<string, ProgramMapping> = {};
  for (const p of CERTIFICATION_PROGRAMS) {
    map[p.code] = {
      industry: p.industry,
      level: p.level,
      onlineProgramId: p.linked_online_program_id,
      pricePlanId: p.price_plan_id,
      renewalProgramId: p.renewal_price_plan_id,
    };
  }
  return map;
}

export const SentinelMapping: Record<string, ProgramMapping> = buildMapping();

/** Look up a mapping by certification code. */
export function getMapping(code: string): ProgramMapping | undefined {
  return SentinelMapping[code];
}

/** Look up a mapping by program ID (slug). */
export function getMappingByProgramId(programId: string): ProgramMapping | undefined {
  const program = CERTIFICATION_PROGRAMS.find((p) => p.id === programId);
  return program ? SentinelMapping[program.code] : undefined;
}

/** Look up the program by certification code. */
export function getProgramByCode(code: string): CertificationProgram | undefined {
  return CERTIFICATION_PROGRAMS.find((p) => p.code === code);
}

/** True if the online program ID is present and non-empty. */
export function hasOnlineProgram(code: string): boolean {
  const m = SentinelMapping[code];
  return !!m && !!m.onlineProgramId && m.onlineProgramId.length > 0;
}

/** True if the pricing plan ID is present and non-empty. */
export function hasPricePlan(code: string): boolean {
  const m = SentinelMapping[code];
  return !!m && !!m.pricePlanId && m.pricePlanId.length > 0;
}

/** True if the renewal program ID is present and non-empty. */
export function hasRenewalProgram(code: string): boolean {
  const m = SentinelMapping[code];
  return !!m && !!m.renewalProgramId && m.renewalProgramId.length > 0;
}

/** Validate that all IDs for a code are configured. Returns null if valid, error message if not. */
export function validateMapping(code: string): string | null {
  const m = SentinelMapping[code];
  if (!m) return "Program configuration incomplete. Contact Sentinel support.";
  if (!m.onlineProgramId) return "Program configuration incomplete. Contact Sentinel support.";
  if (!m.pricePlanId) return "Invalid program configuration. Please verify mapping table.";
  if (!m.renewalProgramId) return "Invalid program configuration. Please verify mapping table.";
  return null;
}

/** Get all mappings as an array (for admin display). */
export function getAllMappings(): { code: string; mapping: ProgramMapping }[] {
  return Object.entries(SentinelMapping).map(([code, mapping]) => ({ code, mapping }));
}

/** Count how many mappings are fully configured. */
export function getConfiguredCount(): { total: number; configured: number; incomplete: number } {
  const codes = Object.keys(SentinelMapping);
  const configured = codes.filter((c) => validateMapping(c) === null).length;
  return { total: codes.length, configured, incomplete: codes.length - configured };
}
